/**
 * Builds the mobile (sections/header.liquid) and desktop
 * (sections/announcement-bar.liquid) country/currency picker lists.
 *
 * The country list is NOT inlined in the page anymore: it is fetched on
 * demand via `?sections=country-data` (sections/country-data.liquid renders
 * snippets/localization-country-data.liquid server-side, so
 * `localization.available_countries` stays the single source of truth).
 * Only `data-current-iso` is inlined, by layout/theme.liquid. Both the
 * fetch URL and the sessionStorage cache are locale-scoped — country names
 * are localized, so switching languages re-fetches instead of serving the
 * previous locale's list.
 *
 * Fetch is triggered by proximity to either picker (pointer/focus entering
 * the desktop switcher or the mobile localization submenu), and retried on
 * the next proximity event if it failed. If a list is already cached, it is
 * populated immediately on DOMContentLoaded.
 *
 * Markup output intentionally mirrors what each section used to render
 * server-side, including two pre-existing quirks preserved for parity:
 * - mobile shows the current country (with a checkmark) in both the
 *   popular and alphabetical buckets if it happens to be flagged popular;
 * - desktop can render AU/CA twice in the popular bucket if Shopify also
 *   flags them popular, since the original template checked `popular?` and
 *   `iso == 'AU' || iso == 'CA'` as two independent conditions.
 *
 * Shopify's country.name arrives already HTML-escaped (e.g. "Antigua &amp;
 * Barbuda" — "&" is entity-encoded in the drop itself; matching Dawn, which
 * renders it raw), so it is inserted without escapeHtml(). Escaping it again
 * made those names display a literal "&amp;". Other fields (iso codes,
 * currency symbols) are not pre-escaped and stay escaped.
 */
(function () {
  // Country names are localized server-side (country.name renders in the
  // storefront locale), so the cache key and the fetch URL are both scoped
  // to the active locale: switching languages re-fetches automatically.
  var LOCALE = (window.Shopify && window.Shopify.locale) || "en";
  var LOCALE_PREFIX = window._erj_.localeRoot();
  var CACHE_KEY = "localization:countries:" + LOCALE;
  var SECTION_URL = LOCALE_PREFIX + "?sections=country-data";
  var PROXIMITY_SELECTOR = [
    ".header-location-switcher",
    "#submenuLocalization",
    "[data-submenu_item='submenuLocalization']",
    "[data-localization-list]"
  ].join(", ");

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (ch) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch];
    });
  }

  function readCurrentIso() {
    var el = document.getElementById("localization-country-data");
    return el ? el.dataset.currentIso : undefined;
  }

  function readCachedCountries() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function fetchCountries() {
    return fetch(SECTION_URL)
      .then(function (response) {
        if (!response.ok) throw new Error("HTTP " + response.status);
        return response.json();
      })
      .then(function (payload) {
        var html = payload && payload["country-data"];
        if (!html) throw new Error("country-data section missing from response");
        var doc = new DOMParser().parseFromString(html, "text/html");
        var el = doc.getElementById("localization-country-data");
        if (!el || !el.textContent.trim()) throw new Error("country data island missing");
        var countries = JSON.parse(el.textContent);
        try {
          sessionStorage.setItem(CACHE_KEY, el.textContent);
        } catch (e) {
          /* private mode etc. — in-memory promise still caches for this page */
        }
        return countries;
      });
  }

  var countriesPromise = null;

  function ensureCountries() {
    if (!countriesPromise) {
      var cached = readCachedCountries();
      countriesPromise = cached
        ? Promise.resolve(cached)
        : fetchCountries().catch(function (err) {
            countriesPromise = null; // re-arm so the next proximity event retries
            console.error("localization-picker: failed to load country data", err);
            return null;
          });
    }
    return countriesPromise;
  }

  function buildMobileList(data, labels) {
    var currentIso = data.currentIso;

    function itemHtml(country) {
      var checkmark = country.iso === currentIso ? '<span class="listCheckmark">&#10003;</span>' : "";
      return (
        '<li class="openedLinks" data-country-iso="' +
        escapeHtml(country.iso) +
        '"><span>' +
        country.name +
        " (" +
        escapeHtml(country.currencyIso) +
        " " +
        escapeHtml(country.currencySymbol) +
        ")</span>" +
        checkmark +
        "</li>"
      );
    }

    var html = '<li class="locationCategory"><span>' + escapeHtml(labels.popular) + "</span></li>";
    data.countries.forEach(function (country) {
      if (country.popular) html += itemHtml(country);
    });
    html += '<li class="locationCategory"><span>' + escapeHtml(labels.alphabetical) + "</span></li>";
    data.countries.forEach(function (country) {
      html += itemHtml(country);
    });
    return html;
  }

  function buildDesktopList(data, labels) {
    var currentIso = data.currentIso;
    var categoryLabelStyle =
      "font-size: 13px;font-weight: bold;padding-top: 20px;padding-inline-start: 20px;text-transform: uppercase;";

    function popularItemHtml(country) {
      return (
        '<li><a href="#" class="country-selector-link" data-country-iso="' +
        escapeHtml(country.iso) +
        '">' +
        country.name +
        "<span>(" +
        escapeHtml(country.currencyIso) +
        "&nbsp;" +
        escapeHtml(country.currencySymbol) +
        ")</span></a></li>"
      );
    }

    function auCaItemHtml(country) {
      return (
        '<li><a href="#" class="country-selector-link" data-country-iso="' +
        escapeHtml(country.iso) +
        '">' +
        country.name +
        " (" +
        escapeHtml(country.currencyIso) +
        " " +
        escapeHtml(country.currencySymbol) +
        ")</a></li>"
      );
    }

    function alphaItemHtml(country) {
      return (
        '<li><a href="#" class="country-selector-link" data-country-iso="' +
        escapeHtml(country.iso) +
        '">' +
        country.name +
        "<span>(" +
        escapeHtml(country.currencyIso) +
        "&nbsp;" +
        escapeHtml(country.currencySymbol) +
        ")</span></a></li>"
      );
    }

    var html = '<li style="' + categoryLabelStyle + '">' + escapeHtml(labels.popular) + "<hr></li>";
    data.countries.forEach(function (country) {
      if (country.iso === currentIso) return;
      if (country.popular) html += popularItemHtml(country);
      if (country.iso === "AU" || country.iso === "CA") html += auCaItemHtml(country);
    });
    html += '<li style="' + categoryLabelStyle + '">' + escapeHtml(labels.alphabetical) + "<hr></li>";
    data.countries.forEach(function (country) {
      if (country.iso === currentIso) return;
      html += alphaItemHtml(country);
    });
    return html;
  }

  var populated = false;

  var LOADING_FALLBACK = "Finding available currencies\u2026";

  function loadingItemHtml(label) {
    return '<li class="localization-loading"><span>' + escapeHtml(label) + "</span></li>";
  }

  function showLoading() {
    document.querySelectorAll("[data-localization-list]").forEach(function (el) {
      el.innerHTML = loadingItemHtml(el.dataset.labelLoading || LOADING_FALLBACK);
    });
  }

  function hideLoading() {
    document.querySelectorAll("[data-localization-list] .localization-loading").forEach(function (el) {
      el.remove();
    });
  }

  function populate(countries) {
    if (populated || !countries || !countries.length) return;
    populated = true;
    var data = { currentIso: readCurrentIso(), countries: countries };

    document.querySelectorAll('[data-localization-list="mobile"]').forEach(function (el) {
      el.innerHTML = buildMobileList(data, {
        popular: el.dataset.labelPopular,
        alphabetical: el.dataset.labelAlphabetical,
      });
    });

    document.querySelectorAll('[data-localization-list="desktop"]').forEach(function (el) {
      el.innerHTML = buildDesktopList(data, {
        popular: el.dataset.labelPopular,
        alphabetical: el.dataset.labelAlphabetical,
      });
    });

    document.dispatchEvent(new CustomEvent("localization-picker:ready"));
  }

  function buildLists() {
    var cached = readCachedCountries();
    if (cached && !populated) {
      populate(cached); // sessionStorage hit — no loading state to flash
      return;
    }
    if (!populated) showLoading();
    ensureCountries().then(function (countries) {
      if (countries) {
        populate(countries); // replaces the loading placeholder via innerHTML
      } else {
        hideLoading(); // fetch failed — clear the placeholder, re-arm retries
        armProximity();
      }
    });
  }

  function onProximity(e) {
    if (!e.target || !e.target.closest || !e.target.closest(PROXIMITY_SELECTOR)) return;
    disarmProximity();
    buildLists();
  }

  function armProximity() {
    document.addEventListener("pointerover", onProximity, true);
    document.addEventListener("focusin", onProximity, true);
  }

  function disarmProximity() {
    document.removeEventListener("pointerover", onProximity, true);
    document.removeEventListener("focusin", onProximity, true);
  }

  function onReady(fn) {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fn);
    else fn();
  }

  onReady(function () {
    var cached = readCachedCountries();
    if (cached) {
      populate(cached);
    } else {
      armProximity();
    }
  });
})();
